import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { IPerson, TPersonValidationErrors } from "interfaces/interfaces";
import { UserAPI } from "API/userApi";

import { useContextHospi } from "context/ContextHospi";
import {
  LOGIN_ROUTE,
  STUDENT_PROFILE_EDIT_ROUTE,
  STUDENT_PROFILE_VIEW_ROUTE
} from "utils/constants";

import { generateErrorResponse } from "utils/helpers";
import { WizardStepPersonalInfoView } from "../../../components/Wizard/Student/Steps/WizardStepPersonalInfo";
import { WizardStepExpectationsView } from "../../../components/Wizard/Student/Steps/WizardStepExpectations";
import { WizardStepAboutMyselfView } from "../../../components/Wizard/Student/Steps/WizardStepAboutMyself";
import { WizardStepAvatarView } from "../../../components/Wizard/Student/Steps/WizardStepAvatar";
import { WizardStepMatchExpectationsView } from "../../../components/Wizard/Student/Steps/WizardStepMatchExpectations";
import { WizardStepSearchPreferencesView } from "../../../components/Wizard/Student/Steps/WizardStepSearchPreferences";
import { Button } from "antd";
import {
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../components/Wizard/utils";
import { isEmpty } from "lodash";
import {
  Content,
  Footer,
  FooterButtons,
  Header,
  PageTitle,
  Root,
  SectionContainer,
  SectionHeader,
  Sections,
  SectionTitle
} from "./styles";
import { TextButton } from "../../../components/Wizard/Student/styles";
import { useHospiNavigate } from "../../../utils/common";
import {
  T_WIZARD_STUDENT_STEP,
  WIZARD_STUDENT_STEPS
} from "../../../components/Wizard/interfaces";
import * as H from "history";
import { UserProfilePasswordSection } from "../UserProfilePasswordSection";
import { UserProfileEmailPreferencesSection } from "../UserProfileEmailPreferencesSection";
import { TAdditionalSections } from "../HostProfileEdit";
import { useNotifier } from "../../../context/ContextNotifier";
import { useConfirmModal } from "../../../context/ContextConfirmModal";
import { scrollToFormWithError } from "../../../utils/dom";

type TAllSections = T_WIZARD_STUDENT_STEP | TAdditionalSections;

export const SectionHeaderComponent = <TStep,>({
  title,
  stepName,
  onEdit,
  showEditButton,
  translationNamespace
}: {
  title?: string;
  stepName?: TStep;
  onEdit?: (e: React.MouseEvent<HTMLButtonElement>, stepName: TStep) => void;
  showEditButton: boolean;
  translationNamespace?: string;
}) => {
  const { t } = useTranslation(translationNamespace);
  return (
    <SectionHeader>
      <SectionTitle>
        {title ??
          (stepName &&
            t(`wizard.steps.${stepName}.titleOverview`, {
              defaultValue: ""
            }))}
      </SectionTitle>
      {showEditButton && stepName && onEdit ? (
        <TextButton
          onClick={(e) => {
            onEdit(e, stepName);
          }}
        >
          {t(`${translationNamespace}Profile.generic.edit`)}
        </TextButton>
      ) : null}
    </SectionHeader>
  );
};

export const StudentProfileEdit: React.FC<{
  viewonly?: boolean;
  canCancel?: boolean;
  canDelete?: boolean;
}> = ({ viewonly = false, canCancel = false, canDelete = false }) => {
  const { i18n } = useTranslation("student");
  const navigate = useHospiNavigate();
  const location: H.Location<{ stepName?: T_WIZARD_STUDENT_STEP } | null> =
    useLocation();
  const [errors, setErrors] = useState<TPersonValidationErrors>({});

  const { userProfile, updateProfile, currentUserId } = useContextHospi();

  const [isLoading, setIsLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const stepsRefs = useRef<{
    [key in T_WIZARD_STUDENT_STEP]?: HTMLDivElement | null;
  }>({});

  const readonly = isLoading;

  const [editedProfile, setEditedProfile] = useState<IPerson | null>(null);

  useEffect(() => {
    if (userProfile) {
      setEditedProfile(userProfile);
    }
  }, [userProfile]);

  if (!localStorage.getItem("student_account_confirmation_executed")) {
    // Add the code block to the dataLayer
    window.dataLayer.push({
      event: "account_confirmation_student",
      user_id: currentUserId,
      user_type: "student"
    });

    // Set the flag in localStorage to indicate that the code has been executed
    localStorage.setItem("student_account_confirmation_executed", "true");
  }

  useEffect(() => {
    if (
      userProfile?.not_provided_fields &&
      userProfile?.not_provided_fields?.length > 0
    ) {
      const newErrors: TPersonValidationErrors = {};

      let error_message: string;
      if (i18n.language === "nl") {
        error_message = "Verplicht";
      } else {
        error_message = "Required";
      }

      userProfile?.not_provided_fields?.forEach((f) => {
        newErrors[f as keyof IPerson] = error_message;
      });

      setErrors(newErrors);
    }
  }, [userProfile]);

  const sendFromData = useCallback(async () => {
    if (!editedProfile) {
      return;
    }
    setIsLoading(true);
    const userFormData: Partial<IPerson> = {
      user_id: currentUserId,
      first_name: editedProfile.first_name,
      last_name: editedProfile.last_name,
      phone_code: editedProfile.phone_code,
      phone_number: editedProfile.phone_number,
      gender: editedProfile.gender,
      spoken_languages: editedProfile.spoken_languages,
      default_language: i18n.language,
      description: editedProfile.description,
      privacy: editedProfile.privacy,
      user_type: editedProfile.user_type,
      birth_date: editedProfile.birth_date,
      birth_country: editedProfile.birth_country,
      study: editedProfile.study,
      past_experience: editedProfile.past_experience,
      autonomy: editedProfile.autonomy,
      categorization: editedProfile.categorization,
      propose: editedProfile.propose,
      ideal_match: editedProfile.ideal_match,
      match_characteristics: editedProfile.match_characteristics,
      amount_looking_people: editedProfile.amount_looking_people,

      // search preferences
      preferred_move_in: editedProfile.preferred_move_in,
      preferred_move_out: editedProfile.preferred_move_out,
      max_budget: editedProfile.max_budget,
      min_area: editedProfile.min_area,
      search_location_string: editedProfile.search_location_string,
      radius_in_meters: editedProfile.radius_in_meters,

      email_preferences_chat: editedProfile.email_preferences_chat,
      email_preferences_general: editedProfile.email_preferences_general,
      email_preferences_room: editedProfile.email_preferences_room,
      email_preferences_match: editedProfile.email_preferences_match
    };
    try {
      const changedFields = getChangedFields(userProfile!, userFormData);
      let updatedUser: IPerson | undefined;
      if (!isEmpty(changedFields)) {
        updatedUser = await UserAPI.updateStudent(
          userProfile?.user_id!,
          getChangedFields(userProfile!, userFormData)
        );
      }
      if (avatarFile) {
        const { thumbnail_url } = await UserAPI.uploadAvatar(
          currentUserId!,
          avatarFile,
          {
            showNotificationOnError: false
          }
        );
        updatedUser = updatedUser
          ? { ...updatedUser, thumbnail_url }
          : { ...editedProfile, thumbnail_url };
      }
      if (updatedUser) {
        updateProfile(updatedUser);
      }
      setIsLoading(false);
      if (!updatedUser || updatedUser?.not_provided_fields?.length === 0) {
        navigate(STUDENT_PROFILE_VIEW_ROUTE);
      }
    } catch (e) {
      setIsLoading(false);
      const errorResponse = generateErrorResponse(e);
      if (errorResponse.hasOwnProperty("status")) {
        if (errorResponse.status === 401) {
          navigate(LOGIN_ROUTE);
        }
        setErrors(getStepErrorsFromResponse(e) as TPersonValidationErrors);

        scrollToFormWithError(rootRef.current!);
      }
    }
    window.dataLayer.push({
      event: "account_finished_student",
      user_id: currentUserId,
      user_type: "student"
    });
  }, [
    avatarFile,
    currentUserId,
    editedProfile,
    i18n.language,
    navigate,
    updateProfile,
    userProfile
  ]);

  const handleEdit = (
    _: React.MouseEvent<HTMLButtonElement>,
    stepName: TAllSections
  ) => {
    navigate(STUDENT_PROFILE_EDIT_ROUTE, {
      state: {
        stepName: stepName,
        noNeedScrollTop: true
      }
    });
  };

  useEffect(() => {
    if (location.state?.stepName) {
      setTimeout(() => {
        if (
          location.state?.stepName &&
          stepsRefs.current[location.state.stepName]
        ) {
          stepsRefs.current[location.state.stepName]?.scrollIntoView();
        }
      }, 500);
    }
  }, [location.state?.stepName]);

  const handleAvatarChange = useCallback((file: File | null) => {
    setAvatarFile(file);
  }, []);

  const handleCancel = useCallback(() => {
    navigate(STUDENT_PROFILE_VIEW_ROUTE);
  }, [navigate]);

  return (
    <Root ref={rootRef}>
      {editedProfile ? (
        <StudentProfileEditView
          values={editedProfile}
          setValues={setEditedProfile}
          onAvatarChange={handleAvatarChange}
          stepsRefs={stepsRefs}
          onEdit={handleEdit}
          viewonly={viewonly}
          readonly={readonly}
          canCancel={canCancel}
          isLoading={isLoading}
          errors={errors}
          onCancel={handleCancel}
          onSubmit={sendFromData}
          canDelete={canDelete}
        />
      ) : null}
    </Root>
  );
};

const StudentProfileEditView = ({
  values,
  setValues,
  onEdit,
  onAvatarChange,
  onCancel,
  onSubmit,
  errors,
  stepsRefs,
  viewonly,
  readonly,
  canCancel,
  canDelete,
  isLoading
}: {
  values: IPerson;
  setValues: React.Dispatch<React.SetStateAction<IPerson | null>>;
  onAvatarChange: (file: File | null) => void;
  stepsRefs: MutableRefObject<{
    [key in TAllSections]?: HTMLDivElement | null;
  }>;
  onEdit: (
    e: React.MouseEvent<HTMLButtonElement>,
    stepName: TAllSections
  ) => void;
  onCancel: () => void;
  onSubmit: () => void;
  viewonly: boolean;
  readonly: boolean;
  canCancel: boolean;
  canDelete: boolean;
  isLoading: boolean;
  errors: TPersonValidationErrors;
}) => {
  const { t } = useTranslation("student");
  const { logOut } = useContextHospi();
  const notifier = useNotifier();
  const confirmModal = useConfirmModal();

  return (
    <Content>
      <Header>
        <PageTitle>{t("viewTitle")}</PageTitle>
      </Header>
      <Sections $isProfileViewOnly={viewonly}>
        <SectionContainer
          ref={(el) => {
            stepsRefs.current[WIZARD_STUDENT_STEPS.Avatar] = el;
          }}
        >
          <SectionHeaderComponent
            stepName={WIZARD_STUDENT_STEPS.Avatar}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="student"
          />
          <WizardStepAvatarView
            avatarThumbnailUrl={values.thumbnail_url}
            errors={null}
            onFileChange={onAvatarChange}
            readonly={readonly}
            viewonly={viewonly}
          />
        </SectionContainer>
        <SectionContainer
          ref={(el) => {
            stepsRefs.current[WIZARD_STUDENT_STEPS.PersonalInfo] = el;
          }}
        >
          <SectionHeaderComponent
            stepName={WIZARD_STUDENT_STEPS.PersonalInfo}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="student"
          />
          <WizardStepPersonalInfoView
            values={useMemo(
              () => ({
                first_name: values.first_name,
                last_name: values.last_name,
                birth_date: values.birth_date,
                birth_country: values.birth_country,
                phone_code: values.phone_code,
                phone_number: values.phone_number,
                gender: values.gender
              }),
              [
                values.birth_country,
                values.birth_date,
                values.first_name,
                values.gender,
                values.last_name,
                values.phone_code,
                values.phone_number
              ]
            )}
            errors={errors}
            setValues={useCallback(
              (values) => {
                setValues(
                  (prev) =>
                    ({
                      ...prev,
                      ...values
                    } as IPerson)
                );
              },
              [setValues]
            )}
            readonly={readonly}
            viewonly={viewonly}
          />
        </SectionContainer>
        <SectionContainer
          ref={(el) => {
            stepsRefs.current[WIZARD_STUDENT_STEPS.Expectations] = el;
          }}
        >
          <SectionHeaderComponent
            stepName={WIZARD_STUDENT_STEPS.Expectations}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="student"
          />
          <WizardStepExpectationsView
            values={useMemo(
              () => ({
                study: values.study,
                propose: values.propose,
                past_experience: values.past_experience
              }),
              [values.past_experience, values.propose, values.study]
            )}
            errors={errors}
            setValues={useCallback(
              (values) => {
                setValues(
                  (prev) =>
                    ({
                      ...prev,
                      ...values
                    } as IPerson)
                );
              },
              [setValues]
            )}
            readonly={readonly}
            viewonly={viewonly}
          />
        </SectionContainer>
        <SectionContainer
          ref={(el) => {
            stepsRefs.current[WIZARD_STUDENT_STEPS.AboutMyself] = el;
          }}
        >
          <SectionHeaderComponent
            stepName={WIZARD_STUDENT_STEPS.AboutMyself}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="student"
          />
          <WizardStepAboutMyselfView
            values={useMemo(
              () => ({
                categorization: values.categorization,
                spoken_languages: values.spoken_languages,
                description: values.description
              }),
              [
                values.categorization,
                values.description,
                values.spoken_languages
              ]
            )}
            errors={errors}
            setValues={useCallback(
              (values) => {
                setValues(
                  (prev) =>
                    ({
                      ...prev,
                      ...values
                    } as IPerson)
                );
              },
              [setValues]
            )}
            readonly={readonly}
            viewonly={viewonly}
          />
        </SectionContainer>

        <SectionContainer
          ref={(el) => {
            stepsRefs.current[WIZARD_STUDENT_STEPS.MatchExpectations] = el;
          }}
        >
          <SectionHeaderComponent
            stepName={WIZARD_STUDENT_STEPS.MatchExpectations}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="student"
          />
          <WizardStepMatchExpectationsView
            values={useMemo(
              () => ({
                match_characteristics: values.match_characteristics,
                ideal_match: values.ideal_match
              }),
              [values.ideal_match, values.match_characteristics]
            )}
            errors={errors}
            setValues={useCallback(
              (values) => {
                setValues(
                  (prev) =>
                    ({
                      ...prev,
                      ...values
                    } as IPerson)
                );
              },
              [setValues]
            )}
            readonly={readonly}
            viewonly={viewonly}
          />
        </SectionContainer>
        <SectionContainer
          ref={(el) => {
            stepsRefs.current[WIZARD_STUDENT_STEPS.SearchPreferences] = el;
          }}
        >
          <SectionHeaderComponent
            stepName={WIZARD_STUDENT_STEPS.SearchPreferences}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="student"
          />
          <WizardStepSearchPreferencesView
            values={useMemo(
              () => ({
                preferred_move_in: values.preferred_move_in,
                preferred_move_out: values.preferred_move_out,
                max_budget: values.max_budget,
                min_area: values.min_area,
                search_location_string: values.search_location_string,
                amount_looking_people: values.amount_looking_people
              }),
              [
                values.amount_looking_people,
                values.max_budget,
                values.min_area,
                values.preferred_move_in,
                values.preferred_move_out,
                values.search_location_string
              ]
            )}
            errors={errors}
            setValues={useCallback(
              (values) => {
                setValues(
                  (prev) =>
                    ({
                      ...prev,
                      ...values
                    } as IPerson)
                );
              },
              [setValues]
            )}
            readonly={readonly}
            viewonly={viewonly}
          />
        </SectionContainer>
        <UserProfilePasswordSection />
        <SectionContainer
          ref={(el) => {
            stepsRefs.current["EmailPreferences"] = el;
          }}
        >
          <SectionHeaderComponent<TAllSections>
            stepName={"EmailPreferences"}
            title={t(`email_preferences.title`)}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="host"
          />
          <UserProfileEmailPreferencesSection
            values={{
              email_preferences_match: values.email_preferences_match,
              email_preferences_general: values.email_preferences_general,
              email_preferences_chat: values.email_preferences_chat,
              email_preferences_room: values.email_preferences_room
            }}
            onChange={(values) => {
              setValues((prev) => {
                if (!prev) {
                  return null;
                }

                return {
                  ...prev,
                  ...values
                };
              });
            }}
            readonly={viewonly || readonly}
          />
        </SectionContainer>
      </Sections>
      <Footer>
        <FooterButtons>
          {canDelete ? (
            <Button
              danger
              size="large"
              onClick={() => {
                confirmModal.show({
                  title: t("studentProfile.deleteAccount.title"),
                  content: t("studentProfile.deleteAccount.text"),
                  onConfirm: async function () {
                    await UserAPI.delete(values?.user_id!);
                    notifier.success({
                      message: t("studentProfile.confirmDeleteModal.deleted")
                    });
                    logOut();
                  },
                  confirmButtonText: t(
                    "studentProfile.deleteAccount.buttonDelete"
                  ),
                  confirmButtonProps: { danger: true },
                  cancelButtonText: t("studentProfile.deleteAccount.buttonKeep")
                });
              }}
            >
              {t("studentProfile.buttonRemove.title")}
            </Button>
          ) : null}
          <div
            style={{
              flexGrow: 1
            }}
          />
          {!viewonly ? (
            <>
              {canCancel ? (
                <Button size="large" onClick={onCancel}>
                  {t("studentProfile.buttonCancel.title")}
                </Button>
              ) : null}
              <Button
                size="large"
                onClick={onSubmit}
                type={"primary"}
                loading={isLoading}
              >
                {t("studentProfile.buttonSave.title")}
              </Button>
            </>
          ) : null}
        </FooterButtons>
      </Footer>
    </Content>
  );
};
